/**
 * utils/contactImportRead.js
 * Contacts xlsx import, batch 5a (the READING-LAYER FIX `contactImportParser.js` itself calls
 * for): `sheet_to_json(ws, {header:1, raw:true|false, defval:null})` collapses a real Excel
 * ERROR cell (`{t:"e", v, w}`) to a bare `null`/`undefined` before `raw`/`defval` are even
 * consulted — see `contactImportParser.js`'s own `isErrorCell` header comment. That collapse
 * destroys the "broken formula" (`#N/A`) vs. "genuinely blank" distinction before the parser
 * ever sees a row array; `evaluateSalesCell`/`isErrorCell` can only recover it when the RAW
 * worksheet cell (`ws[address]`) is substituted back in first. This is that substitution —
 * done once, here, by whichever caller reads the workbook (`ImportKontakWizard.js`), so the
 * parser never needs to know about SheetJS's own worksheet object at all.
 *
 * Scoped to exactly the ONE column `contactImportParser.js`'s `evaluateSalesCell` ever inspects
 * for this shape — Sales, column index 2 on BOTH the CABANG and PUSAT layouts
 * (`contactImportParser.js`'s own `CABANG_COLUMNS`/`PUSAT_COLUMNS`). Every other column is
 * read/stored as plain text regardless of cell shape, so patching them would be pure
 * unused work.
 *
 * Pure: takes an already-extracted `rows` array (SheetJS `sheet_to_json(ws, {header:1, ...})`
 * shape) plus the RAW worksheet (`wb.Sheets[name]`) it came from, returns a NEW rows array with
 * the Sales cell of any row where `sheet_to_json` produced `null`/`undefined` at that position
 * substituted with the raw worksheet cell, but ONLY when that raw cell is actually error-shaped
 * (`{t:"e"}`) — a genuinely blank cell has no entry in `ws` at all (sparse), so this never
 * fabricates a value for an honestly-empty Sales cell. Never mutates `rows` or any row within
 * it; rows/entries that need no patch are returned by reference, unchanged.
 */
import { utils as xlsxUtils } from "xlsx";

const SALES_COLUMN_INDEX = 2;

/**
 * @param {object} ws — the raw SheetJS worksheet object (`wb.Sheets[name]`), address-keyed.
 * @param {any[][]} rows — `sheet_to_json(ws, {header:1, raw:true, defval:null})`'s own output.
 * @returns {any[][]} a new rows array; only rows that actually needed a patch are new arrays.
 */
export function patchSalesErrorCells(ws, rows) {
  if (!Array.isArray(rows)) return rows;
  return rows.map((row, i) => {
    if (!Array.isArray(row)) return row;
    if (row[SALES_COLUMN_INDEX] != null) return row;
    const cell = ws ? ws[xlsxUtils.encode_cell({ r: i, c: SALES_COLUMN_INDEX })] : null;
    if (!cell || cell.t !== "e") return row;
    const patched = row.slice();
    patched[SALES_COLUMN_INDEX] = cell;
    return patched;
  });
}

export { SALES_COLUMN_INDEX };
